<template>
  <canvas ref="canvas" class="particle-flow-bg"></canvas>
</template>

<script>
/**
 * ParticleFlowBackground — 全屏底层粒子流动画背景
 *
 * 大量细小青蓝色半透明粒子，缓慢斜向流动
 * 粒子间微弱连线，边界缓慢消失重生
 * 整体流动舒缓，无剧烈弹跳，低透明度
 * requestAnimationFrame 驱动，resize 自适应
 */
export default {
  name: 'ParticleFlowBackground',
  data () {
    return {
      rafId: null,
      w: 0,
      h: 0,
      dpr: 1,
      particles: [],
      // 粒子数量按屏幕面积自适应，限制上限避免卡顿
      maxParticles: 80,
      // 连线最大距离
      linkDist: 120
    }
  },
  mounted () {
    this.resize()
    window.addEventListener('resize', this.resize)
    this.initParticles()
    this.animate()
  },
  beforeUnmount () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    resize () {
      this.dpr = Math.min(window.devicePixelRatio || 1, 2)
      this.w = window.innerWidth
      this.h = window.innerHeight
      const c = this.$refs.canvas
      c.width = this.w * this.dpr
      c.height = this.h * this.dpr
      c.style.width = this.w + 'px'
      c.style.height = this.h + 'px'
      const ctx = c.getContext('2d')
      ctx.scale(this.dpr, this.dpr)
    },
    initParticles () {
      this.particles = []
      // 斜向流动方向：右下 45°
      for (let i = 0; i < this.maxParticles; i++) {
        this.particles.push(this.createParticle())
      }
    },
    createParticle (respawn) {
      return {
        x: respawn ? -20 : Math.random() * this.w,
        y: respawn ? -20 : Math.random() * this.h,
        // 斜向缓慢移动：0.15~0.4 px/帧
        vx: 0.15 + Math.random() * 0.25,
        vy: 0.15 + Math.random() * 0.25,
        r: 1 + Math.random() * 1.5,
        alpha: 0.15 + Math.random() * 0.20,
        // 生命值：用于淡入淡出
        life: respawn ? 0 : 1,
        maxLife: 1,
        // 生命周期：到达边界时 life 递减
        fading: false
      }
    },
    animate () {
      const c = this.$refs.canvas
      if (!c) return
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, this.w, this.h)

      const ps = this.particles

      // 更新粒子位置
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i]
        p.x += p.vx
        p.y += p.vy

        // 边界检测：到达边界时开始淡出
        if (p.x > this.w + 10 || p.y > this.h + 10) {
          p.fading = true
        }

        // 淡入
        if (p.life < p.maxLife && !p.fading) {
          p.life += 0.02
        }
        // 淡出
        if (p.fading) {
          p.life -= 0.02
          // 完全消失后重生
          if (p.life <= 0) {
            const np = this.createParticle(true)
            np.life = 0
            np.fading = false
            ps[i] = np
          }
        }
      }

      // 绘制连线（微弱，仅近距离粒子）
      for (let i = 0; i < ps.length; i++) {
        const a = ps[i]
        if (a.life < 0.3) continue
        for (let j = i + 1; j < ps.length; j++) {
          const b = ps[j]
          if (b.life < 0.3) continue
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < this.linkDist) {
            // 连线透明度随距离递减 + 随粒子生命值衰减
            const lineAlpha = (1 - dist / this.linkDist) * 0.08 * Math.min(a.life, b.life)
            ctx.strokeStyle = `rgba(79, 195, 247, ${lineAlpha})`
            ctx.lineWidth = 0.6
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }
      }

      // 绘制粒子
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i]
        const a = p.alpha * p.life
        if (a < 0.01) continue
        ctx.beginPath()
        ctx.fillStyle = `rgba(79, 195, 247, ${a})`
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fill()
      }

      this.rafId = requestAnimationFrame(this.animate)
    }
  }
}
</script>

<style scoped>
.particle-flow-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
}
</style>
